import { Check } from 'lucide-react';
import React from 'react';
import { REVIEW_STEPS } from '../constants/review-steps.constants';

interface ApplicationReviewStepperProps {
  activeStep: number;
  visitedSteps: Set<number>;
  onStepClick: (step: number) => void;
  allStepsVisited?: boolean;
}

export const ApplicationReviewStepper: React.FC<ApplicationReviewStepperProps> = ({
  activeStep,
  visitedSteps,
  onStepClick,
  allStepsVisited = false,
}) => {
  console.log('[ApplicationReviewStepper] Rendering stepper:', {
    activeStep,
    visitedSteps: Array.from(visitedSteps),
    allStepsVisited,
  });

  return (
    <div className="w-full">
      {/* Desktop: Horizontal Stepper */}
      <div className="hidden md:flex items-center justify-between">
        {REVIEW_STEPS.map((step, index) => {
          const isCompleted = visitedSteps.has(step.id);
          const isActive = activeStep === step.id;
          // Navigation libre : toutes les étapes sont toujours cliquables
          const isClickable = true;

          return (
            <React.Fragment key={step.id}>
              {/* Step Item */}
              <div className="flex flex-col items-center gap-2 flex-1">
                <button
                  onClick={() => {
                    if (isClickable) {
                      console.log('[ApplicationReviewStepper] Step button clicked:', step.id);
                      onStepClick(step.id);
                    }
                  }}
                  disabled={!isClickable}
                  className={`
                    relative flex items-center justify-center w-10 h-10 rounded-full
                    transition-all duration-200
                    ${isClickable ? 'cursor-pointer hover:scale-105' : 'cursor-not-allowed'}
                    ${isActive ? 'bg-[#00758d] text-white shadow-md' : ''}
                    ${isCompleted && !isActive ? 'bg-emerald-600 text-white' : ''}
                    ${!isActive && !isCompleted ? 'bg-slate-300 text-slate-600' : ''}
                    focus:outline-none focus:ring-2 focus:ring-[#00758d] focus:ring-offset-2
                  `}
                  aria-label={`Étape ${step.id + 1}: ${step.label}`}
                  aria-current={isActive ? 'step' : undefined}
                >
                  {isCompleted && !isActive ? <Check size={20} className="stroke-[2.5]" /> : <step.icon size={20} />}
                </button>

                {/* Label */}
                <span
                  className={`
                    text-sm text-center whitespace-nowrap
                    ${isActive ? 'font-semibold text-slate-900' : ''}
                    ${isCompleted && !isActive ? 'font-medium text-slate-700' : ''}
                    ${!isActive && !isCompleted ? 'text-slate-500' : ''}
                  `}
                >
                  {step.label}
                </span>
              </div>

              {/* Connector Line */}
              {index < REVIEW_STEPS.length - 1 && (
                <div className="flex-1 h-0.5 mx-2 mb-8">
                  <div
                    className={`
                      h-full transition-colors duration-300
                      ${visitedSteps.has(step.id) ? 'bg-[#00758d]' : 'bg-slate-200'}
                    `}
                  />
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Mobile: Vertical Stepper */}
      <div className="md:hidden space-y-4">
        {REVIEW_STEPS.map((step, index) => {
          const isCompleted = visitedSteps.has(step.id);
          const isActive = activeStep === step.id;
          // Navigation libre : toutes les étapes sont toujours cliquables
          const isClickable = true;

          return (
            <div key={step.id} className="flex items-start gap-4">
              {/* Icon Column */}
              <div className="flex flex-col items-center">
                <button
                  onClick={() => {
                    if (isClickable) {
                      console.log('[ApplicationReviewStepper] Step button clicked:', step.id);
                      onStepClick(step.id);
                    }
                  }}
                  disabled={!isClickable}
                  className={`
                    flex items-center justify-center w-10 h-10 rounded-full
                    transition-all duration-200
                    ${isClickable ? 'cursor-pointer' : 'cursor-not-allowed'}
                    ${isActive ? 'bg-[#00758d] text-white shadow-md' : ''}
                    ${isCompleted && !isActive ? 'bg-emerald-600 text-white' : ''}
                    ${!isActive && !isCompleted ? 'bg-slate-300 text-slate-600' : ''}
                    focus:outline-none focus:ring-2 focus:ring-[#00758d] focus:ring-offset-2
                  `}
                  aria-label={`Étape ${step.id + 1}: ${step.label}`}
                  aria-current={isActive ? 'step' : undefined}
                >
                  {isCompleted && !isActive ? <Check size={20} className="stroke-[2.5]" /> : <step.icon size={20} />}
                </button>

                {/* Vertical Connector */}
                {index < REVIEW_STEPS.length - 1 && (
                  <div className="w-0.5 h-12 mt-2">
                    <div
                      className={`
                        w-full h-full transition-colors duration-300
                        ${visitedSteps.has(step.id) ? 'bg-[#00758d]' : 'bg-slate-200'}
                      `}
                    />
                  </div>
                )}
              </div>

              {/* Label Column */}
              <div className="flex-1 pt-2">
                <span
                  className={`
                    text-sm block
                    ${isActive ? 'font-semibold text-slate-900' : ''}
                    ${isCompleted && !isActive ? 'font-medium text-slate-700' : ''}
                    ${!isActive && !isCompleted ? 'text-slate-500' : ''}
                  `}
                >
                  {step.label}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
